import {Injectable} from '@angular/core';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {
  clinicPatientsStateDefaults,
  clinicPatientsStateUniqueIdentifier,
  DashboardClinicPatientsStateModel,
} from './dashboard-clinic-patients.model';
import {
  GrantAccessForPatient,
  GrantAccessForPatientFail,
  GrantAccessForPatientSuccess,
  LoadPatientAccountsByFilters,
  LoadPatientAccountsByName,
  LoadPatientAccountsByPage,
  LoadPatientAccountsBySort,
  LoadPatientAccountsFail,
  LoadPatientAccountsSuccess,
  RemovePatientFromClinic,
  RemovePatientFromClinicFail,
  RemovePatientFromClinicSuccess,
  ResetGrantAccessForPatientIndicators,
} from './dashboard-clinic-patients.actions';
import {DashboardClinicPatientsService} from '../../services/dashboard-clinic-patients/dashboard-clinic-patients.service';
import {PROGRESS_STATUSES} from 'src/app/shared/symbols';
import {BackendError, SuccessMessage} from '../../../messages/messages.actions';
import {UpdateSingleSubmissionSuccess} from '../../../shared/microservices/submission/submission.actions';
import {PATIENTS_ACCOUNTS_ON_PAGE} from '../../symbols/dashboard-clinic-patients-manage-list.symbols';
import {EditPatientSuccess} from '../dashboard-clinic-patients-single-account/dashboard-clinic-patients-single-account.actions';
import {DashboardClinicPatientsSingleAccountState} from '../dashboard-clinic-patients-single-account/dashboard-clinic-patients-single-account.state';
import {DashboardClinicPatientsSingleAccountService} from '../../services/dashboard-clinic-patients-single-account/dashboard-clinic-patients-single-account.service';

@State<DashboardClinicPatientsStateModel>({
  name: clinicPatientsStateUniqueIdentifier,
  defaults: clinicPatientsStateDefaults,
})
@Injectable()
export class DashboardClinicPatientsState {
  constructor(
    private clinicPatients: DashboardClinicPatientsService,
    private clinicPatientsSingleAccount: DashboardClinicPatientsSingleAccountService
  ) {}

  @Selector()
  static loadPatientsStatus(state: DashboardClinicPatientsStateModel): DashboardClinicPatientsStateModel['loadPatientsStatus'] {
    return state.loadPatientsStatus;
  }

  @Selector()
  static pagesCount(state: DashboardClinicPatientsStateModel): DashboardClinicPatientsStateModel['pagesCount'] {
    return state.pagesCount;
  }

  @Selector()
  static currentPage(state: DashboardClinicPatientsStateModel): DashboardClinicPatientsStateModel['currentPage'] {
    return state.currentPage;
  }

  @Selector()
  static patientList(state: DashboardClinicPatientsStateModel): DashboardClinicPatientsStateModel['patientList'] {
    return state.patientList;
  }

  @Selector()
  static removePatientStatus(state: DashboardClinicPatientsStateModel): DashboardClinicPatientsStateModel['removePatientStatus'] {
    return state.removePatientStatus;
  }

  @Selector()
  static appliedSorting(state: DashboardClinicPatientsStateModel): DashboardClinicPatientsStateModel['appliedSorting'] {
    return state.appliedSorting;
  }

  @Selector()
  static appliedFiltering(state: DashboardClinicPatientsStateModel): DashboardClinicPatientsStateModel['appliedFiltering'] {
    return state.appliedFiltering;
  }

  @Selector()
  static grantAccessForPatientStatuses(
    state: DashboardClinicPatientsStateModel
  ): DashboardClinicPatientsStateModel['grantAccessForPatientStatuses'] {
    return state.grantAccessForPatientStatuses;
  }

  @Action(LoadPatientAccountsBySort)
  loadPatientAccountsBySort(ctx: StateContext<DashboardClinicPatientsStateModel>, {sortingRule}: LoadPatientAccountsBySort): void {
    const state = ctx.getState();

    // Prevents from loading the same data twice.
    if (state.loadPatientsStatus === PROGRESS_STATUSES.IN_PROGRESS) {
      return;
    }

    ctx.patchState({
      loadPatientsStatus: PROGRESS_STATUSES.IN_PROGRESS,
    });

    this.clinicPatients.loadSharedAccountsData(state.searchByName, 1, sortingRule, state.appliedFiltering).subscribe({
      next: patientList =>
        ctx.dispatch(
          new LoadPatientAccountsSuccess({
            filteredAccounts: patientList,
            searchString: state.searchByName,
            selectedPage: 1,
            sortingRule: sortingRule,
            filters: state.appliedFiltering,
          })
        ),
      error: error => ctx.dispatch(new LoadPatientAccountsFail(error)),
    });
  }

  @Action(LoadPatientAccountsByName)
  loadPatientAccountsByName(ctx: StateContext<DashboardClinicPatientsStateModel>, {searchString}: LoadPatientAccountsByName): void {
    const state = ctx.getState();

    // Prevents from loading the same data twice.
    if (state.loadPatientsStatus === PROGRESS_STATUSES.IN_PROGRESS) {
      return;
    }

    ctx.patchState({
      loadPatientsStatus: PROGRESS_STATUSES.IN_PROGRESS,
    });

    this.clinicPatients.loadSharedAccountsData(searchString, 1, state.appliedSorting, state.appliedFiltering).subscribe({
      next: patientList =>
        ctx.dispatch(
          new LoadPatientAccountsSuccess({
            filteredAccounts: patientList,
            searchString: searchString,
            selectedPage: 1,
            sortingRule: state.appliedSorting,
            filters: state.appliedFiltering,
          })
        ),
      error: error => ctx.dispatch(new LoadPatientAccountsFail(error)),
    });
  }

  @Action(LoadPatientAccountsByPage)
  loadPatientAccountsByPage(ctx: StateContext<DashboardClinicPatientsStateModel>, {page}: LoadPatientAccountsByPage): void {
    const state = ctx.getState();

    // Prevents from loading the same data twice.
    if (state.loadPatientsStatus === PROGRESS_STATUSES.IN_PROGRESS) {
      return;
    }

    ctx.patchState({
      loadPatientsStatus: PROGRESS_STATUSES.IN_PROGRESS,
    });

    this.clinicPatients.loadSharedAccountsData(state.searchByName, +page, state.appliedSorting, state.appliedFiltering).subscribe({
      next: patientList =>
        ctx.dispatch(
          new LoadPatientAccountsSuccess({
            filteredAccounts: patientList,
            searchString: state.searchByName,
            selectedPage: +page,
            sortingRule: state.appliedSorting,
            filters: state.appliedFiltering,
          })
        ),
      error: error => ctx.dispatch(new LoadPatientAccountsFail(error)),
    });
  }

  @Action(LoadPatientAccountsByFilters)
  loadPatientAccountsByFilters(ctx: StateContext<DashboardClinicPatientsStateModel>, {filters}: LoadPatientAccountsByFilters): void {
    const state = ctx.getState();

    // Prevents from loading the same data twice.
    if (state.loadPatientsStatus === PROGRESS_STATUSES.IN_PROGRESS) {
      return;
    }

    ctx.patchState({
      loadPatientsStatus: PROGRESS_STATUSES.IN_PROGRESS,
    });

    this.clinicPatients.loadSharedAccountsData(state.searchByName, 1, state.appliedSorting, filters).subscribe({
      next: patientList =>
        ctx.dispatch(
          new LoadPatientAccountsSuccess({
            filteredAccounts: patientList,
            searchString: state.searchByName,
            selectedPage: 1,
            sortingRule: state.appliedSorting,
            filters: filters,
          })
        ),
      error: error => ctx.dispatch(new LoadPatientAccountsFail(error)),
    });
  }

  @Action(LoadPatientAccountsSuccess)
  loadPatientAccountsSuccess(ctx: StateContext<DashboardClinicPatientsStateModel>, {payload}: LoadPatientAccountsSuccess): void {
    ctx.patchState({
      loadPatientsStatus: PROGRESS_STATUSES.SUCCEED,
      lastLoadPatientsError: null,
      totalPatientsCount: payload.filteredAccounts.count,
      pagesCount: Math.ceil(payload.filteredAccounts.count / PATIENTS_ACCOUNTS_ON_PAGE) || 1,
      currentPage: payload.selectedPage,
      appliedSorting: payload.sortingRule,
      appliedFiltering: payload.filters,
      patientList: payload.filteredAccounts.results,
      searchByName: payload.searchString,
    });
  }

  @Action(LoadPatientAccountsFail)
  loadPatientAccountsFail(ctx: StateContext<DashboardClinicPatientsStateModel>, {error}: LoadPatientAccountsFail): void {
    ctx.patchState({
      loadPatientsStatus: PROGRESS_STATUSES.INTERRUPTED,
      lastLoadPatientsError: error,
    });

    ctx.dispatch(new BackendError(error));
  }

  @Action(EditPatientSuccess)
  editPatientSuccess(ctx: StateContext<DashboardClinicPatientsStateModel>, {patientId, patientData}: EditPatientSuccess): void {
    // Extract data stored in the state.
    const currentPatientList = ctx.getState().patientList;

    // Update related stored data if its id matches.
    const updatedPatientList = currentPatientList.map(patient => (patient.uid === patientId ? {...patient, ...patientData} : patient));

    // Update the state.
    ctx.patchState({
      patientList: updatedPatientList,
    });
  }

  @Action(RemovePatientFromClinic)
  removePatientFromClinic(
    {patchState, dispatch, getState}: StateContext<DashboardClinicPatientsStateModel>,
    {patientId}: RemovePatientFromClinic
  ): void {
    patchState({
      removePatientStatus: {
        ...getState().removePatientStatus,
        [patientId]: PROGRESS_STATUSES.IN_PROGRESS,
      },
      lastRemovePatientError: null,
    });

    this.clinicPatients.removePatientFromClinic(patientId).subscribe({
      next: () => dispatch(new RemovePatientFromClinicSuccess(patientId)),
      error: error => dispatch(new RemovePatientFromClinicFail(patientId, error)),
    });
  }

  @Action(RemovePatientFromClinicSuccess)
  removePatientFromClinicSuccess(
    {dispatch, patchState, getState}: StateContext<DashboardClinicPatientsStateModel>,
    {patientId}: RemovePatientFromClinicSuccess
  ): void {
    patchState({
      removePatientStatus: {
        ...getState().removePatientStatus,
        [patientId]: PROGRESS_STATUSES.SUCCEED,
      },
    });

    // Reload the list as the backend pagination might be changed after the account removal.
    dispatch([new LoadPatientAccountsByName(getState().searchByName), new SuccessMessage(`Patient account removed successfully`)]);
  }

  @Action(RemovePatientFromClinicFail)
  removePatientFromClinicFail(
    {dispatch, patchState, getState}: StateContext<DashboardClinicPatientsStateModel>,
    {patientId, error}: RemovePatientFromClinicFail
  ): void {
    patchState({
      lastRemovePatientError: error,
      removePatientStatus: {
        ...getState().removePatientStatus,
        [patientId]: PROGRESS_STATUSES.INTERRUPTED,
      },
    });

    dispatch(new BackendError(error));
  }

  @Action(UpdateSingleSubmissionSuccess)
  updateSingleSubmissionSuccess(
    {patchState, getState}: StateContext<DashboardClinicPatientsStateModel>,
    {submission}: UpdateSingleSubmissionSuccess
  ): void {
    // Extract provided patient-related data from a list of loaded accounts.
    const patientList = [...getState().patientList];
    const userSharedAccountIdx = patientList.findIndex(account => account.uid === submission.user);

    // Check if the updated submission is related to any user in the filtered list.
    if (userSharedAccountIdx !== -1) {
      // Update the patient-related data in the list.
      patientList[userSharedAccountIdx] = DashboardClinicPatientsSingleAccountState.updateAccountWithSubmissionInspectedData(
        patientList[userSharedAccountIdx],
        submission
      );
      patchState({patientList});
    }
  }

  @Action(GrantAccessForPatient)
  grantAccessForPatient(ctx: StateContext<DashboardClinicPatientsStateModel>, {patientId, patientData}: GrantAccessForPatient): void {
    ctx.patchState({
      grantAccessForPatientStatuses: {...ctx.getState().grantAccessForPatientStatuses, [patientId]: PROGRESS_STATUSES.IN_PROGRESS},
      lastGrantAccessError: null,
    });

    if (!patientData?.release) {
      this.clinicPatientsSingleAccount.editPatient(patientId, {...patientData, release: true}).subscribe({
        next: updatedPatient =>
          ctx.dispatch(new GrantAccessForPatientSuccess(patientId, {...patientData, ...updatedPatient, release: true})),
        error: error => ctx.dispatch(new GrantAccessForPatientFail(patientId, error)),
      });
    } else {
      this.clinicPatients.resendCompleteRegisterEmail(patientId).subscribe({
        next: () => ctx.dispatch(new GrantAccessForPatientSuccess(patientId, {...patientData, release: true})),
        error: error => ctx.dispatch(new GrantAccessForPatientFail(patientId, error)),
      });
    }
  }

  @Action(GrantAccessForPatientSuccess)
  grantAccessForPatientSuccess(
    ctx: StateContext<DashboardClinicPatientsStateModel>,
    {patientId, patientData}: GrantAccessForPatientSuccess
  ): void {
    // Extract data stored in the state.
    const currentPatientList = ctx.getState().patientList;

    // Update related stored data if its id matches.
    const updatedPatientList = currentPatientList.map(patient => (patient.uid === patientId ? {...patient, ...patientData} : patient));

    // Update the state.
    ctx.patchState({
      grantAccessForPatientStatuses: {...ctx.getState().grantAccessForPatientStatuses, [patientId]: PROGRESS_STATUSES.SUCCEED},
      lastGrantAccessError: null,
      patientList: updatedPatientList,
    });
  }

  @Action(GrantAccessForPatientFail)
  grantAccessForPatientFail(ctx: StateContext<DashboardClinicPatientsStateModel>, {patientId, error}: GrantAccessForPatientFail): void {
    ctx.patchState({
      grantAccessForPatientStatuses: {...ctx.getState().grantAccessForPatientStatuses, [patientId]: PROGRESS_STATUSES.INTERRUPTED},
      lastGrantAccessError: error,
    });

    ctx.dispatch(new BackendError(error));
  }

  @Action(ResetGrantAccessForPatientIndicators)
  resetGrantAccessForPatientIndicators({patchState}: StateContext<DashboardClinicPatientsStateModel>): void {
    patchState({
      grantAccessForPatientStatuses: clinicPatientsStateDefaults.grantAccessForPatientStatuses,
      lastGrantAccessError: clinicPatientsStateDefaults.lastGrantAccessError,
    });
  }
}
